import React from "react";
import { useLocation } from "react-router-dom";
import { Check, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { useApp, telLink, mailLink, waLink, WA_MSG_EN, WA_MSG_AR, PHONE_DISPLAY, EMAIL } from "@/context/AppContext";
import { BRANDS, CATEGORY_KEYS, CATEGORY_LABELS } from "@/i18n";
import { Reveal, SectionHead } from "@/components/Reveal";
import TyreSearch from "@/components/TyreSearch";
import QuotationForm from "@/components/QuotationForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Page = ({ title, sub, children }) => (
  <div className="pt-32 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 min-h-screen">
    <Reveal><SectionHead kicker="Aalam Al-Itarat" title={title} sub={sub} /></Reveal>
    {children}
  </div>
);

export const About = () => {
  const { tr } = useApp();
  return (
    <Page title={tr.aboutTitle}>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div className="space-y-5">
          {tr.about.map((p, i) => <Reveal key={i} delay={i * 0.08}><p className="text-zinc-300 leading-relaxed">{p}</p></Reveal>)}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {tr.aboutManifesto.map((m, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="border border-[#27272a] bg-[#121212] p-6 h-full hover:border-[#EAB308] transition-colors">
                <span className="font-en-head text-4xl font-black text-[#EAB308]">{m.n}</span>
                <h3 className="font-head text-lg font-bold text-white mt-3">{m.t}</h3>
                <p className="text-zinc-400 text-sm mt-2">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <p className="text-zinc-500 text-xs mt-12 max-w-3xl">{tr.brandsDisclaimer}</p>
    </Page>
  );
};

export const Brands = () => {
  const { tr } = useApp();
  return (
    <Page title={tr.brandsTitle} sub={tr.brandsSub}>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {BRANDS.map((b, i) => (
          <Reveal key={b} delay={(i % 5) * 0.04}>
            <div className="border border-[#27272a] bg-[#121212] py-8 text-center hover:border-[#EAB308] transition-colors" data-testid={`brand-${b}`}>
              <span className="font-en-head font-bold text-white uppercase tracking-wide">{b}</span>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="text-zinc-500 text-xs mt-10 max-w-3xl">{tr.brandsDisclaimer}</p>
    </Page>
  );
};

export const Categories = () => {
  const { tr, lang } = useApp();
  return (
    <Page title={tr.categoriesTitle} sub={tr.categoriesSub}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {CATEGORY_KEYS.map((k, i) => (
          <Reveal key={k} delay={(i % 5) * 0.05}>
            <div className="border border-[#27272a] bg-[#121212] p-6 hover:border-[#EAB308] transition-colors group h-full" data-testid={`category-${k}`}>
              <div className="h-1 w-8 bg-[#EAB308] mb-4 group-hover:w-full transition-all duration-300" />
              <span className="text-white font-semibold">{CATEGORY_LABELS[lang][k]}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </Page>
  );
};

export const SearchPage = () => {
  const { tr } = useApp();
  return <Page title={tr.search.title} sub={tr.search.example}><Reveal><TyreSearch full /></Reveal></Page>;
};

export const Services = () => {
  const { tr } = useApp();
  return (
    <Page title={tr.servicesTitle} sub={tr.servicesSub}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tr.services.map((sv, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <div className="border border-[#27272a] bg-[#121212] p-6 hover:border-[#EAB308] transition-colors h-full">
              <div className="flex items-center gap-3"><Check className="h-5 w-5 text-[#006C35]" /><h3 className="text-white font-semibold">{sv.t}</h3></div>
              {sv.n && <p className="text-zinc-500 text-xs mt-2 ms-8">({sv.n})</p>}
            </div>
          </Reveal>
        ))}
      </div>
    </Page>
  );
};

export const Fleet = () => {
  const { tr } = useApp();
  return (
    <Page title={tr.fleet.title} sub={tr.fleet.sub}>
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <div className="space-y-3">
          {tr.fleet.points.map((p, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="flex items-center gap-3 border border-[#27272a] bg-[#121212] px-5 py-4">
                <Check className="h-5 w-5 text-[#006C35] shrink-0" /><span className="text-white text-sm">{p}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}><div className="border border-[#27272a] bg-[#121212] p-6"><QuotationForm /></div></Reveal>
      </div>
    </Page>
  );
};

export const Quotation = () => {
  const { tr } = useApp();
  const loc = useLocation();
  const prefill = loc.state?.prefill || {};
  return (
    <Page title={tr.form.title} sub={tr.form.sub}>
      <div className="max-w-4xl"><Reveal><QuotationForm prefill={prefill} /></Reveal></div>
    </Page>
  );
};

export const FAQ = () => {
  const { tr } = useApp();
  return (
    <Page title={tr.faqTitle}>
      <div className="max-w-3xl">
        <Accordion type="single" collapsible data-testid="faq-page">
          {tr.faqs.map((fq, i) => (
            <AccordionItem key={i} value={`i${i}`} className="border-[#27272a]">
              <AccordionTrigger className="text-white text-start hover:text-[#EAB308] hover:no-underline">{fq.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-400 leading-relaxed">{fq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Page>
  );
};

export const Contact = () => {
  const { tr, lang } = useApp();
  const wa = waLink(lang === "ar" ? WA_MSG_AR : WA_MSG_EN);
  return (
    <Page title={tr.contactTitle} sub={tr.coverage}>
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <a href={telLink} className="flex items-center gap-4 border border-[#27272a] bg-[#121212] p-5 hover:border-[#EAB308] transition-colors"><Phone className="h-6 w-6 text-[#EAB308]" /><div><p className="text-zinc-400 text-xs uppercase">{tr.cta.call}</p><p className="text-white font-bold" dir="ltr">{PHONE_DISPLAY}</p></div></a>
          <a href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 border border-[#27272a] bg-[#121212] p-5 hover:border-[#25D366] transition-colors"><MessageCircle className="h-6 w-6 text-[#25D366]" /><div><p className="text-zinc-400 text-xs uppercase">WhatsApp</p><p className="text-white font-bold" dir="ltr">{PHONE_DISPLAY}</p></div></a>
          <a href={mailLink} className="flex items-center gap-4 border border-[#27272a] bg-[#121212] p-5 hover:border-[#EAB308] transition-colors"><Mail className="h-6 w-6 text-[#EAB308]" /><div><p className="text-zinc-400 text-xs uppercase">Email</p><p className="text-white font-bold">{EMAIL}</p></div></a>
          <div className="flex items-center gap-4 border border-[#27272a] bg-[#121212] p-5"><MapPin className="h-6 w-6 text-[#006C35]" /><div><p className="text-zinc-400 text-xs uppercase">Location</p><p className="text-white font-bold">{tr.hero.head}</p></div></div>
        </div>
        <div className="border border-[#27272a] min-h-[400px]">
          <iframe title="Jeddah Map" className="w-full h-full min-h-[400px]" loading="lazy" src="https://www.google.com/maps?q=Jeddah,Saudi+Arabia&output=embed" />
        </div>
      </div>
    </Page>
  );
};

const LEGAL = {
  privacy: {
    en: ["We respect your privacy. Information you submit (name, contact details, vehicle and tyre requirements) is used only to prepare and share your quotation and to contact you about your request.","We do not sell your personal information. Tyre photos and documents are not permanently stored on our website server or database — please share photos via WhatsApp when needed.","By submitting a request you consent to being contacted by phone, WhatsApp or email regarding your enquiry. You may ask us to remove your data at any time by contacting us.","This site uses standard security measures and spam protection to safeguard submitted information."],
    ar: ["نحن نحترم خصوصيتك. تُستخدم المعلومات التي ترسلها (الاسم وبيانات التواصل ومتطلبات السيارة والإطارات) فقط لإعداد عرض السعر ومشاركته والتواصل معك بخصوص طلبك.","نحن لا نبيع معلوماتك الشخصية. لا يتم تخزين صور الإطارات والمستندات بشكل دائم على خادم الموقع أو قاعدة البيانات — يرجى مشاركة الصور عبر واتساب عند الحاجة.","بإرسالك للطلب فإنك توافق على التواصل معك عبر الهاتف أو واتساب أو البريد الإلكتروني بخصوص استفسارك. يمكنك طلب حذف بياناتك في أي وقت عبر التواصل معنا.","يستخدم الموقع إجراءات أمان قياسية وحماية من الرسائل المزعجة لحماية المعلومات المُرسَلة."]
  },
  terms: {
    en: ["Aalam Al-Itarat is an independent multi-brand tyre quotation service. We are not an official distributor or authorized dealer of any tyre manufacturer unless stated otherwise.","All quotations are subject to availability and confirmation. Prices, brands and options may vary and are provided after reviewing your request.","We do not guarantee lowest prices, guaranteed availability or guaranteed delivery times. Any delivery, alignment, balancing or mobile services are subject to availability.","All trademarks and brand names belong to their respective owners. By using this website you agree to these terms."],
    ar: ["عالم الإطارات خدمة مستقلة متعددة العلامات التجارية لعروض أسعار الإطارات. لسنا موزعاً رسمياً أو وكيلاً معتمداً لأي شركة مصنعة للإطارات ما لم يُذكر خلاف ذلك.","جميع عروض الأسعار تخضع للتوفر والتأكيد. قد تختلف الأسعار والعلامات والخيارات ويتم تقديمها بعد مراجعة طلبك.","لا نضمن أقل الأسعار أو التوفر أو مواعيد التوصيل. أي خدمات توصيل أو ميزان أو موازنة أو خدمة متنقلة تخضع للتوفر.","جميع العلامات التجارية والأسماء ملك لأصحابها. باستخدامك لهذا الموقع فإنك توافق على هذه الشروط."]
  }
};

export const Privacy = () => {
  const { tr, lang } = useApp();
  return <Page title={tr.privacyTitle}><div className="max-w-3xl space-y-5">{LEGAL.privacy[lang].map((p, i) => <Reveal key={i} delay={i * 0.06}><p className="text-zinc-300 leading-relaxed">{p}</p></Reveal>)}</div></Page>;
};

export const Terms = () => {
  const { tr, lang } = useApp();
  return <Page title={tr.termsTitle}><div className="max-w-3xl space-y-5">{LEGAL.terms[lang].map((p, i) => <Reveal key={i} delay={i * 0.06}><p className="text-zinc-300 leading-relaxed">{p}</p></Reveal>)}</div></Page>;
};
